/**
 * Communities layer (W.2) — the operator-facing render of the W.1 Wellness &
 * Headcount keystone object. Per operator-cop-doctrine §3B.8 communities live in
 * the AMBIENT register: a fixed community glyph, never a pulse. The ONLY thing
 * that varies is the glyph fill, which encodes the community's latest wellness-
 * check status (styles/communityStatus) — neutral slate when no check exists yet.
 *
 * #351 (founder ruling 2026-06-18) — ONE community symbol for every community (no
 * First-Nation / town split): a group-of-people glyph (iconRegistry COMMUNITY_GLYPH),
 * replacing the prior `home` glyph that read as a single building.
 *
 * Mounted under TWO hazard-group toggles (Flood Risk → Flood-Vulnerable
 * Communities, Ice Storm Risk → Vulnerable Communities); both back the same
 * /api/communities data, so MapView gates this single layer on `showCommunities`
 * (the OR of the two toggles).
 *
 * The W.1 list endpoint is intentionally light (no wellness/headcount), so status
 * + the headcount badge chip are supplied by the caller via `getStatus` /
 * `getBadge` (App's client-side detail cache). The layer reports its in-view
 * community ids through `onInView` so the cache can lazily fill, then `recolor()`
 * re-paints in place once statuses arrive — no refetch, no pulse.
 *
 * W.2.1 (Task #91): the list endpoint now carries `latest_wellness_status` +
 * `latest_headcount_summary` per feature, so the layer paints coloured on FIRST
 * paint straight from the feature props — no per-community detail fetch, no
 * grey-then-recolor. The caller's `getStatus`/`getBadge` cache is still honoured
 * as a *fresher* override (e.g. right after an operator edits a wellness check),
 * but is no longer required for the initial colour.
 *
 * Imperative installer (MapView owns the viewer): requestRefresh on camera moves,
 * setVisible on the toggle, pick from the central click handler → CommunityFeature
 * → the community detail panel.
 */
import * as Cesium from 'cesium';
import type { WellnessCheckStatus } from '@tsr/shared-types';
import { fetchCommunities, type BboxQuery, type CommunityFeature, type CommunityFeatureCollection } from '../api';
import { communityStatusStyle } from '../styles/communityStatus';
import { cimdQuintileStyle } from '../styles/cimd';
import { renderLucideToDataUrl, COMMUNITY_GLYPH } from '../iconRegistry';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

const REFRESH_DEBOUNCE_MS = 300;
const COMMUNITY_PREFIX = 'community:';
/** Ambient community glyph size (px). Fixed — communities never pulse (§3B.8). */
const COMMUNITY_GLYPH_SIZE = 20;
/** The headcount badge chip only paints at regional zoom and closer (the chip is
 *  detail-level context, not a country-view signal). Eye-distance cap in metres. */
const BADGE_FAR_M = 1_400_000;
/** #350 — the community glyph itself self-gates to regional+ zoom (mirroring how
 *  power-outages / road-closures tier in via REGIONAL_VISIBLE_FAR_M), so an
 *  enabled communities layer no longer clutters the country view. This far cap
 *  (2.0e6) is deliberately FARTHER than BADGE_FAR_M (1.4e6): the marker is always
 *  present whenever its headcount chip wants to show, so the gate can never hide a
 *  community at a zoom where the chip should appear (the §3B.8 regression guard). */
const MARKER_FAR_M = REGIONAL_VISIBLE_FAR_M;

export interface CommunitiesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Latest wellness-check status for a community id (null → neutral ambient). */
  getStatus?: (id: string) => WellnessCheckStatus | null;
  /** Compact headcount chip text for a community id (null → no chip). */
  getBadge?: (id: string) => string | null;
  /** Reports the community ids currently rendered, so the caller can lazily fill
   *  its status/badge cache and then call `recolor()`. */
  onInView?: (ids: string[]) => void;
  /** Task #189 — the default-OFF "Vulnerability shading" sub-toggle. When true the
   *  marker chip appends the derived social-vulnerability band and tints the chip
   *  text the band colour (ambient register; the band LABEL rides with the colour,
   *  Law 1). Toggle changes re-apply via `recolor()` — no refetch, no motion. */
  getVulnerabilityShading?: () => boolean;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<CommunityFeatureCollection>;
}

export interface CommunitiesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Re-apply glyph fill + headcount chip from getStatus/getBadge, in place. */
  recolor(): void;
  pick(entity: unknown): CommunityFeature | null;
  destroy(): void;
}

interface CommunityEntry {
  feature: CommunityFeature;
  marker: Cesium.Entity;
}

export function installCommunitiesLayer(
  viewer: Cesium.Viewer,
  opts: CommunitiesLayerOptions = {},
): CommunitiesLayerHandle {
  const fetcher = opts.fetcher ?? fetchCommunities;
  const ds = new Cesium.CustomDataSource('communities');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, CommunityFeature>();
  const entries = new Map<string, CommunityEntry>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  /** Wellness status for one community: a fresher caller-cache value wins (e.g.
   *  just after an edit), else the feature's own W.2.1 list-endpoint status. */
  const statusFor = (feature: CommunityFeature): WellnessCheckStatus | null => {
    const cached = opts.getStatus ? opts.getStatus(feature.id) : null;
    return cached ?? feature.properties.latest_wellness_status ?? null;
  };

  /** Headcount chip text: a fresher caller-cache badge wins, else built from the
   *  feature's own W.2.1 headcount summary (null → no chip). */
  const badgeFor = (feature: CommunityFeature): string | null => {
    const cached = opts.getBadge ? opts.getBadge(feature.id) : null;
    if (cached) return cached;
    return headcountBadge(
      feature.properties.name,
      feature.properties.latest_headcount_summary,
      feature.properties.population,
    );
  };

  /** Status colour for one community (null status → neutral ambient). */
  const colorFor = (feature: CommunityFeature): Cesium.Color =>
    Cesium.Color.fromCssColorString(communityStatusStyle(statusFor(feature)).color);

  /** Chip text + fill for one community (#234 CIMD shading-aware; null → chip off). */
  const chipFor = (feature: CommunityFeature): CommunityChip | null =>
    communityChip(
      feature.properties.name,
      badgeFor(feature),
      feature.properties.cimd_situational_vulnerability_q,
      opts.getVulnerabilityShading?.() ?? false,
    );

  /** Glyph billboard image tinted to the community's status colour. Returns null
   *  in headless tests (no canvas) → caller falls back to a coloured point. The
   *  glyph is the §3B.8 ambient community symbol — a group-of-people glyph (#351),
   *  one symbol for every community (not the old single-building `home`). */
  const glyphFor = (feature: CommunityFeature): string | null =>
    renderLucideToDataUrl(COMMUNITY_GLYPH, {
      color: communityStatusStyle(statusFor(feature)).color,
      size: COMMUNITY_GLYPH_SIZE,
    });

  const render = (fc: CommunityFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    entries.clear();
    // #350 — self-gate the community glyph to regional+ zoom; hidden at country zoom.
    const gate = new Cesium.DistanceDisplayCondition(0, MARKER_FAR_M);
    for (const feature of fc.features) {
      const id = feature.id;
      const [lon, lat] = feature.geometry.coordinates;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      const glyph = glyphFor(feature);
      const chip = chipFor(feature);
      const marker = ds.entities.add({
        id: `${COMMUNITY_PREFIX}${id}`,
        position,
        // Ambient register: a fixed-size community glyph, never pulsing. Fill
        // encodes wellness status. No canvas (unit tests) → fall back to a dot.
        ...(glyph
          ? {
              billboard: {
                image: glyph,
                width: COMMUNITY_GLYPH_SIZE,
                height: COMMUNITY_GLYPH_SIZE,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
                verticalOrigin: Cesium.VerticalOrigin.CENTER,
                distanceDisplayCondition: gate,
              },
            }
          : {
              point: {
                pixelSize: COMMUNITY_GLYPH_SIZE * 0.7,
                color: colorFor(feature),
                outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
                outlineWidth: 1,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
                distanceDisplayCondition: gate,
              },
            }),
        label: {
          text: chip?.text ?? '',
          show: !!chip,
          font: '600 12px Inter, system-ui, sans-serif',
          fillColor: chipFillColor(chip),
          showBackground: true,
          backgroundColor: Cesium.Color.fromCssColorString('#0b1020').withAlpha(0.75),
          pixelOffset: new Cesium.Cartesian2(0, -COMMUNITY_GLYPH_SIZE),
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          // Chip is detail-level: only at regional zoom and closer.
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, BADGE_FAR_M),
        },
      });
      entityToFeature.set(marker, feature);
      entries.set(id, { feature, marker });
    }
    opts.onInView?.(fc.features.map((f) => f.id));
  };

  const recolor = (): void => {
    for (const [, entry] of entries) {
      const glyph = glyphFor(entry.feature);
      if (glyph && entry.marker.billboard) {
        entry.marker.billboard.image = new Cesium.ConstantProperty(glyph);
      } else if (entry.marker.point) {
        entry.marker.point.color = new Cesium.ConstantProperty(colorFor(entry.feature));
      }
      const chip = chipFor(entry.feature);
      if (entry.marker.label) {
        entry.marker.label.text = new Cesium.ConstantProperty(chip?.text ?? '');
        entry.marker.label.show = new Cesium.ConstantProperty(!!chip);
        entry.marker.label.fillColor = new Cesium.ConstantProperty(chipFillColor(chip));
      }
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchCommunities failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    recolor,
    pick(entity: unknown): CommunityFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      entries.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}

/** Default chip text colour (matches the panel's slate-200 body text). */
const CHIP_TEXT_DEFAULT = '#e2e8f0';
/** Law 4 ceiling for the band-tinted chip text (ambient — never full-pop). */
const CHIP_TINT_ALPHA = 0.85;

export interface CommunityChip {
  text: string;
  /** CSS hex for the label fill. */
  color: string;
  /** True when `color` is a vulnerability-band tint (rendered at ≤0.85 α, Law 4). */
  tinted: boolean;
}

/**
 * Task #234 (re-keying #189) — chip content for one community. With shading OFF
 * this is exactly the headcount badge (chip off when no headcount). With the
 * default-OFF "Vulnerability shading" sub-toggle ON, every community shows a
 * chip: the headcount badge (or just the name) with its CIMD 2021 (StatCan)
 * Situational Vulnerability quintile appended, tinted the quintile colour — the
 * quintile TEXT always rides with the colour (Law 1), missing coverage reads
 * "CIMD UNKNOWN" (honest, never fabricated), and nothing moves (Law 4).
 */
export function communityChip(
  name: string,
  badge: string | null,
  cimdSituationalVulnerabilityQ: number | null,
  shading: boolean,
): CommunityChip | null {
  if (!shading) return badge ? { text: badge, color: CHIP_TEXT_DEFAULT, tinted: false } : null;
  const s = cimdQuintileStyle(cimdSituationalVulnerabilityQ);
  const cimdText = s.quintile !== null ? `CIMD SV Q${s.quintile}/5` : 'CIMD UNKNOWN';
  return { text: `${badge ?? name} · ${cimdText}`, color: s.color, tinted: true };
}

/** Cesium fill colour for a chip (tinted band colours clamp to the Law 4 α). */
export function chipFillColor(chip: CommunityChip | null): Cesium.Color {
  if (!chip) return Cesium.Color.fromCssColorString(CHIP_TEXT_DEFAULT);
  const c = Cesium.Color.fromCssColorString(chip.color);
  return chip.tinted ? c.withAlpha(CHIP_TINT_ALPHA) : c;
}

/** Compact headcount chip text, e.g. "Kashechewan · EVAC 1,847 / 1,950".
 *  Returns null when there's no headcount to show (keeps the chip off). */
export function headcountBadge(
  name: string,
  headcount: { evacuated: number } | null,
  population: number | null,
): string | null {
  if (!headcount) return null;
  const evac = headcount.evacuated.toLocaleString();
  const pop = population != null ? ` / ${population.toLocaleString()}` : '';
  return `${name} · EVAC ${evac}${pop}`;
}
